import React from 'react';
import { classifyReferral, resolvePageIntent } from './ContentBehaviorTabs';
import { Skeleton } from '../shared/Skeleton';

const INSIGHT_ICONS = {
  source: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  mobile: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  ),
  calendar: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  page: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  conversions: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" />
    </svg>
  ),
  trophy: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><rect x="6" y="2" width="12" height="12" rx="2" /><path d="M8 22v-4a4 4 0 0 1 8 0v4" /><path d="M12 14v2" />
    </svg>
  ),
  share: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  ),
};

const TONE_STYLES = {
  blue:    { border: 'border-t-[#32B1FF]',                      iconBg: 'bg-[#32B1FF]/10 text-[#32B1FF]' },
  indigo:  { border: 'border-t-indigo-400 dark:border-t-indigo-500', iconBg: 'bg-indigo-400/10 text-indigo-500 dark:text-indigo-400' },
  amber:   { border: 'border-t-amber-400 dark:border-t-amber-500',   iconBg: 'bg-amber-400/10 text-amber-600 dark:text-amber-400' },
  sky:     { border: 'border-t-sky-400 dark:border-t-sky-500',       iconBg: 'bg-sky-400/10 text-sky-600 dark:text-sky-400' },
  emerald: { border: 'border-t-emerald-400 dark:border-t-emerald-500', iconBg: 'bg-emerald-400/10 text-emerald-600 dark:text-emerald-400' },
  teal:    { border: 'border-t-teal-400 dark:border-t-teal-500',     iconBg: 'bg-teal-400/10 text-teal-600 dark:text-teal-400' },
  purple:  { border: 'border-t-purple-400 dark:border-t-purple-500', iconBg: 'bg-purple-400/10 text-purple-600 dark:text-purple-400' },
  gray:    { border: 'border-t-gray-300 dark:border-t-gray-700',     iconBg: 'bg-gray-200/50 dark:bg-gray-800/60 text-gray-400 dark:text-gray-500' },
};

const InsightCard = ({ icon, title, value, detail, tone = 'blue', emphasize = false }) => {
  const toneStyle = TONE_STYLES[tone] ?? TONE_STYLES.blue;
  return (
    <div className={`relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 border-t-[3px] ${toneStyle.border} rounded-xl p-2.5 sm:p-3.5 flex gap-2 sm:gap-3 items-start ${emphasize ? 'ring-1 ring-emerald-400/30 dark:ring-emerald-500/20' : ''}`}>
      <div className={`shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center ${toneStyle.iconBg}`}>
        {INSIGHT_ICONS[icon] ?? INSIGHT_ICONS.source}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 truncate mb-0.5">{title}</div>
        <div className={`font-bold text-gray-900 dark:text-gray-100 leading-tight ${emphasize ? 'text-base sm:text-lg' : 'text-[13px] sm:text-sm'} truncate`}>{value}</div>
        {detail && <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{detail}</div>}
      </div>
    </div>
  );
};

// Mirrors InsightCard's horizontal layout (icon box + 3 text rows) so the grid
// keeps its shape during load. Uses the shared Skeleton atom for the shimmer.
const SkeletonSlot = () => (
  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 border-t-[3px] border-t-gray-200 dark:border-t-gray-700 rounded-xl p-2.5 sm:p-3.5 flex gap-2 sm:gap-3 items-start">
    <Skeleton className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg" />
    <div className="flex-1 min-w-0 space-y-1.5">
      <Skeleton className="h-3 w-14" />
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-3 w-16" />
    </div>
  </div>
);

const EmptySlot = ({ title }) => (
  <InsightCard icon="source" tone="gray" title={title} value="—" detail="No data for this period" />
);

/* ---------- Slot builders (return a card-props object or null if no content) ---------- */
const buildTopSource = (channelData) => {
  if (!channelData?.length) return null;
  const total = channelData.reduce((sum, row) => sum + (row.sessions || 0), 0);
  const top = [...channelData].sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0))[0];
  if (!top || total <= 0) return null;
  return { icon: 'source', tone: 'blue', title: 'Top Source', value: top.channel, detail: `${((top.sessions / total) * 100).toFixed(0)}% of sessions` };
};

const buildMobile = (devicesData) => {
  if (!devicesData?.length) return null;
  const total = devicesData.reduce((sum, row) => sum + (row.sessions || 0), 0);
  const mobile = devicesData.find(row => (row.device || '').toLowerCase() === 'mobile');
  if (!mobile || total <= 0) return null;
  return { icon: 'mobile', tone: 'indigo', title: 'Mobile', value: `${((mobile.sessions / total) * 100).toFixed(0)}% of visitors`, detail: `${(mobile.sessions || 0).toLocaleString()} sessions` };
};

const DOW_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const buildPeakDay = (dayOfWeekData, hourlyTrafficData) => {
  // Spec (Section 20): the busiest day is computed from the day-of-week aggregate
  // (ga4_day_of_week_traffic), not the daily traffic series — so it stays consistent
  // with the "Traffic by Day of Week" chart, which reads the same window.
  if (!dayOfWeekData?.length) return null;
  const best = [...dayOfWeekData].sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0))[0];
  if (!best || best.day_of_week == null) return null;
  const dayName = DOW_NAMES[best.day_of_week];
  if (!dayName) return null;
  let peakStr = '';
  if (hourlyTrafficData?.length) {
    const peakHour = [...hourlyTrafficData].sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0))[0];
    if (peakHour && peakHour.hour != null) {
      const hour = peakHour.hour;
      peakStr = hour === 0 ? '12am' : hour < 12 ? hour + 'am' : hour === 12 ? '12pm' : (hour - 12) + 'pm';
    }
  }
  return { icon: 'calendar', tone: 'amber', title: 'Peak Day', value: dayName, detail: peakStr ? `peak at ${peakStr}` : '' };
};

const buildHomepage = (pagesData) => {
  if (!pagesData?.length) return null;
  const total = pagesData.reduce((sum, row) => sum + (row.pageviews || 0), 0);
  // Classify via the shared page-intent config (covers /, /index*, /home*) rather than
  // matching literal paths, so non-standard homepage slugs still register. Sum every
  // homepage-intent row since a site can have more than one (e.g. / and /index.html).
  const homeViews = pagesData.reduce(
    (sum, row) => (resolvePageIntent(row.page_path) === 'Homepage' ? sum + (row.pageviews || 0) : sum),
    0,
  );
  if (homeViews <= 0 || total <= 0) return null;
  return { icon: 'page', tone: 'sky', title: 'Homepage', value: `${((homeViews / total) * 100).toFixed(0)}% of pageviews`, detail: 'from homepage' };
};

const buildConversions = (kpis) => {
  if (!kpis || (!(kpis.conversions > 0) && !((kpis.total_revenue || 0) > 0))) return null;
  const rev = kpis.total_revenue;
  return {
    icon: 'conversions', tone: 'emerald', title: 'Conversions',
    value: `${(kpis.conversions || 0).toLocaleString()} conversions`,
    detail: rev > 0 ? `$${Number(rev).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} revenue` : 'this period',
  };
};

const buildTopConverter = (channelData) => {
  if (!channelData?.length) return null;
  const topConv = [...channelData].sort((rowA, rowB) => (rowB.conversions || 0) - (rowA.conversions || 0))[0];
  if (!topConv || (topConv.conversions || 0) <= 0) return null;
  return { icon: 'trophy', tone: 'teal', title: 'Top Converter', value: topConv.channel, detail: `${(topConv.conversions || 0).toLocaleString()} conversions` };
};

const buildTopReferral = (referralsData) => {
  if (!referralsData?.length) return null;
  const byCat = {};
  let total = 0;
  for (const row of referralsData) {
    const category = classifyReferral(row.source);
    const sessions = row.sessions || 0;
    byCat[category] = (byCat[category] || 0) + sessions;
    total += sessions;
  }
  const sorted = Object.entries(byCat).sort((entryA, entryB) => entryB[1] - entryA[1]);
  if (!sorted.length || total <= 0) return null;
  // "Other" is the uncategorized catch-all — never surface it as the headline referral.
  // Prefer the largest real category; only fall back to "Other" if nothing else has sessions.
  const [topCat, topSessions] = sorted.find(([category]) => category !== 'Other') || sorted[0];
  return { icon: 'share', tone: 'purple', title: 'Top Referral', value: topCat, detail: `${((topSessions / total) * 100).toFixed(0)}% of referral traffic` };
};

const IntelligenceCards = ({
  channelData, devicesData, dayOfWeekData, pagesData, kpis, hourlyTrafficData, referralsData, loading,
}) => {
  const slots = [
    // Range-driven (reflect full date range)
    { card: buildTopSource(channelData),                    fallbackTitle: 'Top Source' },
    { card: buildMobile(devicesData),                       fallbackTitle: 'Mobile' },
    { card: buildConversions(kpis),                         fallbackTitle: 'Conversions' },
    { card: buildTopConverter(channelData),                 fallbackTitle: 'Top Converter' },
    // 7-day snapshot (reflect closest pre-computed weekly window)
    { card: buildPeakDay(dayOfWeekData, hourlyTrafficData), fallbackTitle: 'Peak Day' },
    { card: buildHomepage(pagesData),                       fallbackTitle: 'Homepage' },
    { card: buildTopReferral(referralsData),                fallbackTitle: 'Top Referral' },
  ];

  return (
    <div className="grid grid-cols-2 min-[1200px]:grid-cols-4 min-[1800px]:grid-cols-7 gap-2 sm:gap-3">
      {slots.map(({ card, fallbackTitle }, index) => {
        if (loading) return <SkeletonSlot key={index} />;
        if (!card) return <EmptySlot key={index} title={fallbackTitle} />;
        return <InsightCard key={index} {...card} />;
      })}
    </div>
  );
};

export default IntelligenceCards;
